/**
 * PATH: packages/ui/src/components/settings/security-tab/MfaPolicySection.tsx
 * FILE_ID: UI-SETTINGS-MFA-POLICY-001
 * PURPOSE: Org-admin control for the organisation-wide MFA policy.
 * WHY: Admins can require MFA for everyone with a grace window before it is
 *      enforced, and choose which factors are permitted. Backed by
 *      GET/PUT /auth/mfa/policy/.
 * FLOW:
 *   ┌──────────┐   ┌───────────────────────┐   ┌──────────────────────┐
 *   │ policy   │ → │ admin edits require +  │ → │ PUT → resolved policy │
 *   │ (GET)    │   │ grace + methods        │   │ (grace_deadline)      │
 *   └──────────┘   └───────────────────────┘   └──────────────────────┘
 * DEPENDENCIES:
 *   - SettingsApiClient: injected HTTP bridge
 *   - lucide-react: icons
 * RELATED:
 *   - SecurityTab.tsx (parent, admin-gated), MfaMethodsSection.tsx (enrolment).
 */

import { useCallback, useEffect, useState } from 'react'
import { Building2, Loader2, Check } from 'lucide-react'
import type { SettingsApiClient } from '../types'

export interface MfaPolicy {
  required: boolean
  grace_period_days: number
  allowed_methods: string[]
  grace_deadline?: string | null
  enforced_at?: string | null
}

export interface MfaPolicySectionProps {
  apiClient: SettingsApiClient
  endpoint: string
  onChanged?: () => void
}

const ALL_METHODS: { id: string; label: string }[] = [
  { id: 'totp', label: 'Authenticator app' },
  { id: 'sms', label: 'Text message' },
  { id: 'email', label: 'Email' },
]

/** Tolerate both raw bodies and axios-style `{ data }` envelopes. */
function unwrap<T>(response: unknown): T {
  if (response && typeof response === 'object' && 'data' in response) {
    return (response as { data: T }).data
  }
  return response as T
}

/** Pull a human message from an axios error / Error, else the fallback. */
function errMessage(e: unknown, fallback: string): string {
  if (e && typeof e === 'object') {
    const ax = e as { response?: { data?: { detail?: string } }; message?: string }
    if (ax.response?.data?.detail) return ax.response.data.detail
    if (ax.message) return ax.message
  }
  return fallback
}

export function MfaPolicySection({ apiClient, endpoint, onChanged }: MfaPolicySectionProps) {
  const [policy, setPolicy] = useState<MfaPolicy | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    let mounted = true
    apiClient.get(endpoint)
      .then((r) => { if (mounted) setPolicy(unwrap<MfaPolicy>(r)) })
      .catch(() => { if (mounted) setError('Could not load the organisation MFA policy.') })
      .finally(() => { if (mounted) setLoading(false) })
    return () => { mounted = false }
  }, [apiClient, endpoint])

  const update = useCallback((patch: Partial<MfaPolicy>) => {
    setPolicy((prev) => prev ? { ...prev, ...patch } : prev)
    setSaved(false)
  }, [])

  const toggleMethod = useCallback((id: string) => {
    setPolicy((prev) => {
      if (!prev) return prev
      const set = new Set(prev.allowed_methods || [])
      if (set.has(id)) set.delete(id); else set.add(id)
      // Never allow an empty set — keep at least one method enabled.
      const next = Array.from(set)
      return { ...prev, allowed_methods: next.length ? next : prev.allowed_methods }
    })
    setSaved(false)
  }, [])

  const save = useCallback(async () => {
    if (!policy) return
    setSaving(true); setError(''); setSaved(false)
    try {
      const data = unwrap<MfaPolicy>(await apiClient.put(endpoint, {
        required: policy.required,
        grace_period_days: policy.grace_period_days,
        allowed_methods: policy.allowed_methods,
      }))
      setPolicy(data)
      setSaved(true)
      onChanged?.()
    } catch (e: unknown) {
      setError(errMessage(e, 'Could not save the policy.'))
    } finally {
      setSaving(false)
    }
  }, [apiClient, endpoint, policy, onChanged])

  if (loading) {
    return (
      <section className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading organisation policy…
      </section>
    )
  }
  if (!policy) {
    return error ? <section className="text-sm text-destructive">{error}</section> : null
  }

  const deadline = policy.grace_deadline ? new Date(policy.grace_deadline) : null

  return (
    <section className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-primary/10">
          <Building2 className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h3 className="font-semibold text-foreground">Organisation MFA policy</h3>
          <p className="text-sm text-muted-foreground">
            Require two-factor authentication for everyone in your organisation.
          </p>
        </div>
      </div>

      <label className="flex items-start gap-3 p-4 rounded-xl border border-border cursor-pointer">
        <input
          type="checkbox"
          checked={policy.required}
          onChange={(e) => update({ required: e.target.checked })}
          className="mt-0.5 w-4 h-4 rounded border-border"
        />
        <div>
          <p className="font-medium text-foreground">Require MFA for all members</p>
          <p className="text-sm text-muted-foreground">
            Members keep access during the grace window, then must enrol to sign in.
          </p>
        </div>
      </label>

      {policy.required && (
        <div className="space-y-4 pl-1">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Grace period (days)
            </label>
            <input
              type="number"
              min={0}
              max={90}
              value={policy.grace_period_days}
              onChange={(e) => update({ grace_period_days: Math.max(0, Number(e.target.value) || 0) })}
              className="w-32 px-3 py-2 rounded-lg border border-border bg-background"
            />
            <p className="text-xs text-muted-foreground mt-1">
              {policy.grace_period_days === 0
                ? 'Enforced immediately - members must enrol before their next sign-in.'
                : `Members have ${policy.grace_period_days} day(s) to enrol.`}
              {deadline && ` Current deadline: ${deadline.toLocaleDateString()}.`}
            </p>
          </div>

          <div>
            <p className="text-sm font-medium text-foreground mb-2">Allowed methods</p>
            <div className="flex flex-wrap gap-2">
              {ALL_METHODS.map((m) => {
                const on = (policy.allowed_methods || []).includes(m.id)
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => toggleMethod(m.id)}
                    className={`px-3 py-1.5 text-sm rounded-lg border ${
                      on
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    {m.label}
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {error && <p className="text-sm text-destructive">{error}</p>}

      <button
        onClick={save}
        disabled={saving}
        className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <Check className="w-4 h-4" /> : null}
        {saved ? 'Saved' : 'Save policy'}
      </button>
    </section>
  )
}

export default MfaPolicySection
